import { useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowRight,
  CircleDollarSign,
  FolderOpen,
  Gauge,
  LayoutDashboard,
  PanelTop,
  Plus,
  Trash2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { conflictApi } from '@/api/conflictApi'
import { creditsApi } from '@/api/creditsApi'
import { friendlyMessage } from '@/api/http'
import { projectApi } from '@/api/projectApi'
import { pulseApi } from '@/api/pulseApi'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { CreateProjectWizard } from '@/components/project/CreateProjectWizard'
import { SERVICE_COLORS } from '@/lib/serviceColors'
import { ALL_PROJECTS_VALUE } from '@/lib/projectSelection'
import { useAuthStore } from '@/stores/authStore'
import type { CreditsBalance } from '@/api/creditsApi'
import type { ConflictRow, Project } from '@/types/nf'
import type { PulseSummary } from '@/types/pulse'
import { formatCount, formatCredits } from '@/utils/format'

export function activeProjectCount(projects: Project[]) {
  return projects.filter((project) => !project.deleted_at).length
}

export function recentEditedProjects(projects: Project[], limit = 5) {
  return projects
    .filter((project) => !project.deleted_at)
    .slice()
    .sort((a, b) => (b.updated_at ?? 0) - (a.updated_at ?? 0))
    .slice(0, limit)
}

export function inFlightChapterCount(summary: PulseSummary | undefined) {
  return (summary?.projects ?? []).reduce((projectTotal, project) => {
    const stationTotal = Object.values(project.stations).reduce(
      (sum, station) => sum + station.generating + station.running + station.awaiting_confirm,
      0,
    )
    return projectTotal + stationTotal
  }, 0)
}

export function consistencyHealth(conflicts: ConflictRow[]) {
  const active = conflicts.filter((row) => !row.override_reason)
  const hard = active.filter((row) => row.severity === 'hard').length
  const soft = active.filter((row) => row.severity === 'soft').length
  const overridden = conflicts.length - active.length
  return {
    hard,
    soft,
    overridden,
    statusKey: hard > 0 ? 'needsAttention' : 'healthy',
  } as const
}

function projectDate(value: number | null | undefined) {
  if (!value) return null
  const timestampMs = value < 10_000_000_000 ? value * 1000 : value
  const date = new Date(timestampMs)
  return Number.isNaN(date.getTime()) ? null : date
}

function formatRelativeTime(value: number | null | undefined, language: string) {
  const date = projectDate(value)
  if (!date) return '—'
  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000)
  const divisions: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 60 * 60 * 24 * 365],
    ['month', 60 * 60 * 24 * 30],
    ['week', 60 * 60 * 24 * 7],
    ['day', 60 * 60 * 24],
    ['hour', 60 * 60],
    ['minute', 60],
  ]
  const formatter = new Intl.RelativeTimeFormat(language.toLowerCase().startsWith('zh') ? 'zh-CN' : 'en-US', {
    numeric: 'auto',
  })
  for (const [unit, seconds] of divisions) {
    if (Math.abs(diffSeconds) >= seconds) {
      return formatter.format(Math.round(diffSeconds / seconds), unit)
    }
  }
  return formatter.format(diffSeconds, 'second')
}

function LoadingBlock({ className = 'h-28' }: { className?: string }) {
  return <Skeleton className={`${className} w-full rounded-lg`} />
}

function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border border-destructive/35 bg-destructive/10 px-3 py-2 text-sm text-destructive">
      {children}
    </div>
  )
}

function MetricCard({
  to,
  icon: Icon,
  title,
  value,
  detail,
  isLoading,
  tone,
}: {
  to: string
  icon: typeof LayoutDashboard
  title: string
  value: string
  detail: string
  isLoading?: boolean
  tone?: 'normal' | 'warning'
}) {
  return (
    <Link to={to} className="group block rounded-lg focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50">
      <Card className="h-full rounded-lg py-0 transition-colors group-hover:border-primary/50 group-hover:bg-card/90">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
          <Icon className={tone === 'warning' ? 'size-4 text-amber-600' : 'size-4 text-primary'} />
        </CardHeader>
        <CardContent className="p-4 pt-0">
          {isLoading ? <Skeleton className="h-8 w-20" /> : <div className="nf-mono text-2xl font-semibold">{value}</div>}
          <div className="mt-2 flex items-center justify-between gap-3 text-xs text-muted-foreground">
            <span className="min-w-0 truncate">{isLoading ? ' ' : detail}</span>
            <ArrowRight className="size-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
          </div>
        </CardContent>
      </Card>
    </Link>
  )
}

function creditValue(balance: CreditsBalance | undefined, language: string) {
  if (!balance) return '—'
  if (balance.enabled === false) return '∞'
  return formatCredits(balance.available, language)
}

export default function HomeDashboard() {
  const { t, i18n } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const [createOpen, setCreateOpen] = useState(false)
  const language = i18n.resolvedLanguage ?? i18n.language

  const projectsQuery = useQuery({
    queryKey: ['projects', user?.user_id, 'home-dashboard'],
    queryFn: () => projectApi.list(user!.user_id),
    enabled: Boolean(user?.user_id),
  })
  const pulseQuery = useQuery({
    queryKey: ['pulse-summary', 'home-dashboard'],
    queryFn: pulseApi.summary,
    enabled: Boolean(user?.user_id),
  })
  const conflictsQuery = useQuery({
    queryKey: ['conflicts', ALL_PROJECTS_VALUE, 'home-dashboard'],
    queryFn: () => conflictApi.list(ALL_PROJECTS_VALUE),
    enabled: Boolean(user?.user_id),
  })
  const creditsQuery = useQuery({
    queryKey: ['credits', 'balance', user?.user_id, 'home-dashboard'],
    queryFn: () => creditsApi.balance(user?.user_id),
    enabled: Boolean(user?.user_id),
    staleTime: 60_000,
  })

  const projects = projectsQuery.data ?? []
  const recentProjects = useMemo(() => recentEditedProjects(projects), [projects])
  const consistency = consistencyHealth(conflictsQuery.data ?? [])
  const inFlight = inFlightChapterCount(pulseQuery.data)
  const hasError = projectsQuery.error || pulseQuery.error || conflictsQuery.error || creditsQuery.error

  return (
    <section className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="mb-2 flex items-center gap-2 text-sm text-primary">
            <LayoutDashboard className="size-4" />
            {t('home.eyebrow')}
          </p>
          <h1 className="text-2xl font-semibold tracking-normal sm:text-3xl">{t('home.title')}</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{t('home.subtitle')}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" />
          {t('home.quickActions.newProject')}
        </Button>
      </div>

      {hasError ? (
        <div className="mb-4 grid gap-2">
          {projectsQuery.error ? <ErrorNote>{friendlyMessage(projectsQuery.error, t('home.errors.projects'))}</ErrorNote> : null}
          {pulseQuery.error ? <ErrorNote>{friendlyMessage(pulseQuery.error, t('home.errors.pulse'))}</ErrorNote> : null}
          {conflictsQuery.error ? <ErrorNote>{friendlyMessage(conflictsQuery.error, t('home.errors.conflicts'))}</ErrorNote> : null}
          {creditsQuery.error ? <ErrorNote>{friendlyMessage(creditsQuery.error, t('home.errors.credits'))}</ErrorNote> : null}
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          to="/projects"
          icon={FolderOpen}
          title={t('home.metrics.activeProjects.title')}
          value={formatCount(activeProjectCount(projects), language)}
          detail={t('home.metrics.activeProjects.detail')}
          isLoading={projectsQuery.isLoading}
        />
        <MetricCard
          to="/board"
          icon={PanelTop}
          title={t('home.metrics.inFlight.title')}
          value={formatCount(inFlight, language)}
          detail={t('home.metrics.inFlight.detail')}
          isLoading={pulseQuery.isLoading}
        />
        <MetricCard
          to="/consistency"
          icon={consistency.statusKey === 'healthy' ? Gauge : AlertTriangle}
          title={t('home.metrics.consistency.title')}
          value={t(`home.metrics.consistency.status.${consistency.statusKey}`)}
          detail={t('home.metrics.consistency.detail', consistency)}
          isLoading={conflictsQuery.isLoading}
          tone={consistency.statusKey === 'healthy' ? 'normal' : 'warning'}
        />
        <MetricCard
          to="/billing"
          icon={CircleDollarSign}
          title={t('home.metrics.credits.title')}
          value={creditValue(creditsQuery.data, language)}
          detail={creditsQuery.data?.enabled === false ? t('billing.credits.disabled') : t('home.metrics.credits.detail')}
          isLoading={creditsQuery.isLoading}
        />
      </div>

      <div className="mt-6 grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <Card className="rounded-lg py-0">
          <CardHeader className="p-5 pb-0">
            <CardTitle>{t('home.recent.title')}</CardTitle>
            <CardDescription>{t('home.recent.description')}</CardDescription>
          </CardHeader>
          <CardContent className="p-5">
            {projectsQuery.isLoading ? (
              <div className="grid gap-3">
                {Array.from({ length: 5 }).map((_, index) => <LoadingBlock key={index} className="h-14" />)}
              </div>
            ) : recentProjects.length ? (
              <div className="divide-y rounded-lg border">
                {recentProjects.map((project) => (
                  <Link
                    key={project.id}
                    to={`/projects/${project.id}`}
                    className="flex items-center justify-between gap-4 p-4 transition-colors hover:bg-accent/60 focus-visible:bg-accent/60 focus-visible:outline-none"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{project.title}</p>
                      <p className="mt-1 truncate text-xs text-muted-foreground">
                        {project.genre || t('home.recent.genreFallback')}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-3 text-xs text-muted-foreground">
                      <span className="nf-mono">{formatRelativeTime(project.updated_at, language)}</span>
                      <ArrowRight className="size-3.5" />
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="grid min-h-48 place-items-center rounded-lg border border-dashed bg-card/45 p-6 text-center">
                <div>
                  <p className="text-sm font-medium">{t('home.recent.empty')}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{t('home.recent.emptyHint')}</p>
                  <Button className="mt-4" onClick={() => setCreateOpen(true)}>
                    <Plus className="size-4" />
                    {t('home.quickActions.newProject')}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-lg py-0">
          <CardHeader className="p-5 pb-0">
            <CardTitle>{t('home.quickActions.title')}</CardTitle>
            <CardDescription>{t('home.quickActions.description')}</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-2 p-5">
            <Button className="justify-start" onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" />
              {t('home.quickActions.newProject')}
            </Button>
            <Button asChild variant="outline" className="justify-start">
              <Link to="/board">
                <span className="size-2.5 rounded-full" style={{ backgroundColor: SERVICE_COLORS.text }} />
                <PanelTop className="size-4" />
                {t('home.quickActions.board')}
              </Link>
            </Button>
            <Button asChild variant="outline" className="justify-start">
              <Link to="/trash">
                <Trash2 className="size-4" />
                {t('home.quickActions.trash')}
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>

      <CreateProjectWizard open={createOpen} onOpenChange={setCreateOpen} userId={user?.user_id} />
    </section>
  )
}
